import React from 'react';
import {
  GraduationCap,
  BookOpen,
  Play,
  CheckCircle2,
  Clock,
  Users,
  Star,
  Award,
  FileText,
  Video,
  MessageSquare,
  Download,
  Lock,
  ChevronRight,
  BarChart3,
  Trophy,
  Target,
  Flame,
  Calendar,
  CircleDot,
  Sparkles,
  BadgeCheck,
  Medal,
  Zap,
} from 'lucide-react';
import './CoursesMockupContent.css';

const CoursesMockupContent = ({ visibleItems, animationPhase, activeInteriorTab, onTabClick = () => {} }) => {
  // Avatar colors
  const avatarColors = [
    'linear-gradient(135deg, #6366f1 0%, #14b8a6 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    'linear-gradient(135deg, #ec4899 0%, #14b8a6 100%)',
    'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
    'linear-gradient(135deg, #14b8a6 0%, #ec4899 100%)',
  ];

  // Course data
  const courseInfo = {
    title: 'Advanced Machine Learning for Research',
    instructor: 'Dr. Lena Hoffmann',
    instructorRole: 'AI Research Lead @ Stanford',
    rating: 4.9,
    students: '12,847',
    duration: '24 hours',
    modules: 12,
    progress: 68,
    level: 'Advanced',
  };

  // Curriculum modules
  const curriculum = [
    {
      id: 1,
      title: 'Introduction to ML in Research',
      lessons: 4,
      duration: '1h 45m',
      completed: true,
      lessons_data: [
        { title: 'Course Overview', duration: '12:30', completed: true, type: 'video' },
        { title: 'Setting Up Environment', duration: '25:00', completed: true, type: 'video' },
        { title: 'Research ML Fundamentals', duration: '35:20', completed: true, type: 'video' },
        { title: 'Module Quiz', duration: '15 mins', completed: true, type: 'quiz' },
      ]
    },
    {
      id: 2,
      title: 'Deep Learning Architectures',
      lessons: 6,
      duration: '3h 20m',
      completed: true,
      lessons_data: [
        { title: 'Neural Network Basics', duration: '28:45', completed: true, type: 'video' },
        { title: 'CNNs for Image Analysis', duration: '42:15', completed: true, type: 'video' },
        { title: 'RNNs and Transformers', duration: '38:30', completed: true, type: 'video' },
        { title: 'Hands-on Lab: Build a CNN', duration: '45 mins', completed: true, type: 'lab' },
        { title: 'Architecture Selection Guide', duration: '22:00', completed: true, type: 'video' },
        { title: 'Module Assessment', duration: '20 mins', completed: true, type: 'quiz' },
      ]
    },
    {
      id: 3,
      title: 'Data Preprocessing Techniques',
      lessons: 5,
      duration: '2h 15m',
      completed: false,
      current: true,
      progress: 60,
      lessons_data: [
        { title: 'Data Cleaning Strategies', duration: '32:00', completed: true, type: 'video' },
        { title: 'Feature Engineering', duration: '28:45', completed: true, type: 'video' },
        { title: 'Handling Imbalanced Data', duration: '25:30', completed: true, type: 'video', current: true },
        { title: 'Practical Exercise', duration: '30 mins', completed: false, type: 'lab' },
        { title: 'Module Quiz', duration: '15 mins', completed: false, type: 'quiz' },
      ]
    },
    {
      id: 4,
      title: 'Model Training & Optimization',
      lessons: 7,
      duration: '4h 10m',
      completed: false,
      locked: false,
    },
    {
      id: 5,
      title: 'Research Paper Implementation',
      lessons: 5,
      duration: '3h 00m',
      completed: false,
      locked: true,
    },
  ];

  // Assignments
  const assignments = [
    {
      id: 1,
      title: 'CNN Image Classifier',
      type: 'Project',
      dueDate: 'Due Feb 15',
      status: 'submitted',
      grade: '95/100',
      feedback: 'Excellent implementation of transfer learning!',
    },
    {
      id: 2,
      title: 'Data Preprocessing Pipeline',
      type: 'Lab',
      dueDate: 'Due Feb 22',
      status: 'in-progress',
      progress: 45,
    },
    {
      id: 3,
      title: 'Mid-Course Assessment',
      type: 'Quiz',
      dueDate: 'Due Feb 28',
      status: 'upcoming',
      questions: 25,
      timeLimit: '45 mins',
    },
    {
      id: 4,
      title: 'Research Paper Review',
      type: 'Assignment',
      dueDate: 'Due Mar 5',
      status: 'upcoming',
    },
  ];

  // Discussions
  const discussions = [
    {
      id: 1,
      title: 'Best practices for handling missing data in medical datasets?',
      author: 'Henrik R.',
      initials: 'HR',
      replies: 23,
      likes: 45,
      time: '2h ago',
      tags: ['data-preprocessing', 'medical-ai'],
      pinned: true,
    },
    {
      id: 2,
      title: 'Confusion about backpropagation in Lecture 3.2',
      author: 'Zainab L.',
      initials: 'ZL',
      replies: 12,
      likes: 18,
      time: '5h ago',
      tags: ['deep-learning', 'question'],
      answered: true,
    },
    {
      id: 3,
      title: 'Sharing my CNN project - feedback welcome!',
      author: 'Declan K.',
      initials: 'DK',
      replies: 31,
      likes: 67,
      time: '1d ago',
      tags: ['project', 'cnn'],
    },
    {
      id: 4,
      title: 'GPU recommendations for training large models?',
      author: 'Ingrid S.',
      initials: 'IS',
      replies: 19,
      likes: 28,
      time: '2d ago',
      tags: ['hardware', 'tips'],
    },
  ];

  // Resources
  const resources = [
    {
      id: 1,
      title: 'Course Slides - All Modules',
      type: 'PDF',
      size: '45.2 MB',
      icon: FileText,
      color: 'red',
    },
    {
      id: 2,
      title: 'Jupyter Notebooks Collection',
      type: 'ZIP',
      size: '128 MB',
      icon: FileText,
      color: 'amber',
    },
    {
      id: 3,
      title: 'Research Papers Reading List',
      type: 'PDF',
      size: '12.8 MB',
      icon: BookOpen,
      color: 'blue',
    },
    {
      id: 4,
      title: 'Dataset: Medical Imaging',
      type: 'ZIP',
      size: '2.4 GB',
      icon: BarChart3,
      color: 'green',
    },
    {
      id: 5,
      title: 'Cheat Sheet: ML Algorithms',
      type: 'PDF',
      size: '3.2 MB',
      icon: Zap,
      color: 'indigo',
    },
  ];

  // Certificates/Achievements
  const achievements = [
    {
      id: 1,
      title: 'Course Certificate',
      description: 'Complete all modules to earn',
      progress: 68,
      icon: Award,
      color: 'amber',
      locked: false,
    },
    {
      id: 2,
      title: 'Perfect Quiz Score',
      description: 'Score 100% on any quiz',
      earned: true,
      icon: Trophy,
      color: 'indigo',
    },
    {
      id: 3,
      title: 'Fast Learner',
      description: 'Complete 3 modules in one week',
      earned: true,
      icon: Flame,
      color: 'orange',
    },
    {
      id: 4,
      title: 'Community Helper',
      description: 'Answer 10 discussion questions',
      progress: 70,
      icon: MessageSquare,
      color: 'cyan',
      locked: false,
    },
  ];

  // Tabs
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'curriculum', label: 'Curriculum', icon: Play },
    { id: 'assignments', label: 'Assignments', icon: FileText },
    { id: 'discussions', label: 'Discussions', icon: MessageSquare },
    { id: 'resources', label: 'Resources', icon: Download },
    { id: 'certificates', label: 'Certificates', icon: Award },
  ];

  // Render Overview Tab
  const renderOverviewTab = () => (
    <div className="course-overview">
      {/* Progress Card */}
      <div className={`course-progress-card ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="course-progress-header">
          <div className="course-progress-info">
            <span className="course-progress-label">Your Progress</span>
            <span className="course-progress-value">{courseInfo.progress}%</span>
          </div>
          <div className="course-streak">
            <Flame size={12} />
            <span>7 day streak!</span>
          </div>
        </div>
        <div className="course-progress-bar">
          <div className="course-progress-fill" style={{ width: `${courseInfo.progress}%` }}></div>
        </div>
        <div className="course-progress-stats">
          <div className="course-stat-item">
            <CheckCircle2 size={10} />
            <span>8/12 modules</span>
          </div>
          <div className="course-stat-item">
            <Clock size={10} />
            <span>16h completed</span>
          </div>
          <div className="course-stat-item">
            <Target size={10} />
            <span>Est. 8h left</span>
          </div>
        </div>
      </div>

      {/* Continue Learning */}
      <div className={`course-continue-section ${visibleItems >= 2 ? 'visible' : ''}`}>
        <h3 className="course-section-title">
          <Play size={12} />
          Continue Learning
        </h3>
        <div className="course-current-lesson">
          <div className="course-lesson-thumbnail">
            <Video size={16} />
            <div className="course-lesson-play">
              <Play size={10} />
            </div>
          </div>
          <div className="course-lesson-info">
            <span className="course-lesson-module">Module 3 • Lesson 3</span>
            <h4 className="course-lesson-title">Handling Imbalanced Data</h4>
            <div className="course-lesson-meta">
              <span><Clock size={9} /> 25:30</span>
              <span className="course-lesson-progress-text">12:45 remaining</span>
            </div>
          </div>
          <button className="course-resume-btn">
            <Play size={10} />
            Resume
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className={`course-quick-stats ${visibleItems >= 3 ? 'visible' : ''}`}>
        <div className="course-quick-stat">
          <div className="course-quick-stat-icon course-quick-stat-icon--blue">
            <Video size={12} />
          </div>
          <div className="course-quick-stat-info">
            <span className="course-quick-stat-value">42</span>
            <span className="course-quick-stat-label">Video Lessons</span>
          </div>
        </div>
        <div className="course-quick-stat">
          <div className="course-quick-stat-icon course-quick-stat-icon--green">
            <FileText size={12} />
          </div>
          <div className="course-quick-stat-info">
            <span className="course-quick-stat-value">12</span>
            <span className="course-quick-stat-label">Assignments</span>
          </div>
        </div>
        <div className="course-quick-stat">
          <div className="course-quick-stat-icon course-quick-stat-icon--amber">
            <Trophy size={12} />
          </div>
          <div className="course-quick-stat-info">
            <span className="course-quick-stat-value">4</span>
            <span className="course-quick-stat-label">Certificates</span>
          </div>
        </div>
        <div className="course-quick-stat">
          <div className="course-quick-stat-icon course-quick-stat-icon--pink">
            <Users size={12} />
          </div>
          <div className="course-quick-stat-info">
            <span className="course-quick-stat-value">2.4K</span>
            <span className="course-quick-stat-label">Classmates</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Curriculum Tab
  const renderCurriculumTab = () => (
    <div className="course-curriculum">
      <div className="course-curriculum-header">
        <span className="course-curriculum-progress">8 of 12 modules completed</span>
      </div>
      <div className="course-modules-list">
        {curriculum.map((module, index) => (
          <div 
            key={module.id}
            className={`course-module-card ${module.completed ? 'completed' : ''} ${module.current ? 'current' : ''} ${module.locked ? 'locked' : ''} ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.08}s` }}
          >
            <div className="course-module-header">
              <div className="course-module-number">
                {module.completed ? (
                  <CheckCircle2 size={14} />
                ) : module.locked ? (
                  <Lock size={12} />
                ) : (
                  <span>{module.id}</span>
                )}
              </div>
              <div className="course-module-info">
                <h4 className="course-module-title">{module.title}</h4>
                <div className="course-module-meta">
                  <span><Play size={9} /> {module.lessons} lessons</span>
                  <span><Clock size={9} /> {module.duration}</span>
                </div>
              </div>
              {module.current && (
                <div className="course-module-current-badge">
                  <CircleDot size={10} />
                  In Progress
                </div>
              )}
              {module.completed && (
                <div className="course-module-complete-badge">
                  <CheckCircle2 size={10} />
                  Done
                </div>
              )}
            </div>
            {module.current && module.lessons_data && (
              <div className="course-module-lessons">
                {module.lessons_data.map((lesson, i) => (
                  <div key={i} className={`course-lesson-item ${lesson.completed ? 'completed' : ''} ${lesson.current ? 'current' : ''}`}>
                    <div className="course-lesson-status">
                      {lesson.completed ? (
                        <CheckCircle2 size={10} />
                      ) : lesson.current ? (
                        <Play size={10} />
                      ) : (
                        <CircleDot size={10} />
                      )}
                    </div>
                    <span className="course-lesson-name">{lesson.title}</span>
                    <span className="course-lesson-duration">{lesson.duration}</span>
                  </div>
                ))}
              </div>
            )}
            {module.current && module.progress && (
              <div className="course-module-progress">
                <div className="course-module-progress-bar">
                  <div className="course-module-progress-fill" style={{ width: `${module.progress}%` }}></div>
                </div>
                <span className="course-module-progress-text">{module.progress}%</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Render Assignments Tab
  const renderAssignmentsTab = () => (
    <div className="course-assignments">
      <div className="course-assignments-tabs">
        <button className="course-assign-tab active">All</button>
        <button className="course-assign-tab">In Progress</button>
        <button className="course-assign-tab">Submitted</button>
        <button className="course-assign-tab">Upcoming</button>
      </div>
      <div className="course-assignments-list">
        {assignments.map((assignment, index) => (
          <div 
            key={assignment.id}
            className={`course-assignment-card ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className="course-assignment-header">
              <div className={`course-assignment-type course-assignment-type--${assignment.type.toLowerCase()}`}>
                {assignment.type}
              </div>
              <span className={`course-assignment-status course-assignment-status--${assignment.status}`}>
                {assignment.status === 'submitted' && <CheckCircle2 size={10} />}
                {assignment.status === 'in-progress' && <Clock size={10} />}
                {assignment.status === 'upcoming' && <Calendar size={10} />}
                {assignment.status.replace('-', ' ')}
              </span>
            </div>
            <h4 className="course-assignment-title">{assignment.title}</h4>
            <div className="course-assignment-meta">
              <span className="course-assignment-due">
                <Calendar size={10} />
                {assignment.dueDate}
              </span>
              {assignment.questions && (
                <span><FileText size={10} /> {assignment.questions} questions</span>
              )}
              {assignment.timeLimit && (
                <span><Clock size={10} /> {assignment.timeLimit}</span>
              )}
            </div>
            {assignment.status === 'in-progress' && assignment.progress && (
              <div className="course-assignment-progress">
                <div className="course-assignment-progress-bar">
                  <div className="course-assignment-progress-fill" style={{ width: `${assignment.progress}%` }}></div>
                </div>
                <span>{assignment.progress}% complete</span>
              </div>
            )}
            {assignment.status === 'submitted' && (
              <div className="course-assignment-grade">
                <div className="course-grade-badge">
                  <Star size={10} />
                  {assignment.grade}
                </div>
                {assignment.feedback && (
                  <p className="course-assignment-feedback">"{assignment.feedback}"</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Render Discussions Tab
  const renderDiscussionsTab = () => (
    <div className="course-discussions">
      <div className="course-discussions-header">
        <button className="course-new-discussion-btn">
          <MessageSquare size={10} />
          New Discussion
        </button>
      </div>
      <div className="course-discussions-list">
        {discussions.map((discussion, index) => (
          <div 
            key={discussion.id}
            className={`course-discussion-card ${discussion.pinned ? 'pinned' : ''} ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className="course-discussion-avatar" style={{ background: avatarColors[index % avatarColors.length] }}>
              {discussion.initials}
            </div>
            <div className="course-discussion-content">
              <div className="course-discussion-title-row">
                {discussion.pinned && (
                  <span className="course-discussion-pinned">
                    <Sparkles size={8} />
                    Pinned
                  </span>
                )}
                {discussion.answered && (
                  <span className="course-discussion-answered">
                    <CheckCircle2 size={8} />
                    Answered
                  </span>
                )}
                <h4 className="course-discussion-title">{discussion.title}</h4>
              </div>
              <div className="course-discussion-meta">
                <span className="course-discussion-author">{discussion.author}</span>
                <span className="course-discussion-time">{discussion.time}</span>
              </div>
              <div className="course-discussion-tags">
                {discussion.tags.map((tag, i) => (
                  <span key={i} className="course-discussion-tag">{tag}</span>
                ))}
              </div>
              <div className="course-discussion-stats">
                <span><MessageSquare size={10} /> {discussion.replies}</span>
                <span><Star size={10} /> {discussion.likes}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Resources Tab
  const renderResourcesTab = () => (
    <div className="course-resources">
      <div className="course-resources-header">
        <span className="course-resources-count">{resources.length} resources available</span>
      </div>
      <div className="course-resources-grid">
        {resources.map((resource, index) => (
          <div 
            key={resource.id}
            className={`course-resource-card ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className={`course-resource-icon course-resource-icon--${resource.color}`}>
              <resource.icon size={14} />
            </div>
            <div className="course-resource-info">
              <h4 className="course-resource-title">{resource.title}</h4>
              <div className="course-resource-meta">
                <span className="course-resource-type">{resource.type}</span>
                <span className="course-resource-size">{resource.size}</span>
              </div>
            </div>
            <button className="course-resource-download">
              <Download size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Certificates Tab
  const renderCertificatesTab = () => (
    <div className="course-certificates">
      <div className="course-certificates-header">
        <div className="course-cert-progress">
          <Trophy size={14} />
          <span>2 of 4 achievements earned</span>
        </div>
      </div>
      <div className="course-achievements-grid">
        {achievements.map((achievement, index) => (
          <div 
            key={achievement.id}
            className={`course-achievement-card ${achievement.earned ? 'earned' : ''} ${visibleItems >= index + 1 ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
          >
            <div className={`course-achievement-icon course-achievement-icon--${achievement.color}`}>
              <achievement.icon size={18} />
              {achievement.earned && (
                <div className="course-achievement-check">
                  <CheckCircle2 size={10} />
                </div>
              )}
            </div>
            <h4 className="course-achievement-title">{achievement.title}</h4>
            <p className="course-achievement-desc">{achievement.description}</p>
            {achievement.progress && !achievement.earned && (
              <div className="course-achievement-progress">
                <div className="course-achievement-progress-bar">
                  <div className="course-achievement-progress-fill" style={{ width: `${achievement.progress}%` }}></div>
                </div>
                <span>{achievement.progress}%</span>
              </div>
            )}
            {achievement.earned && (
              <div className="course-achievement-earned-badge">
                <BadgeCheck size={10} />
                Earned!
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Certificate Preview */}
      <div className={`course-certificate-preview ${visibleItems >= 5 ? 'visible' : ''}`}>
        <div className="course-certificate-card">
          <div className="course-certificate-border">
            <div className="course-certificate-content">
              <Medal size={24} className="course-certificate-medal" />
              <span className="course-certificate-label">Certificate of Completion</span>
              <h3 className="course-certificate-course">{courseInfo.title}</h3>
              <p className="course-certificate-text">Complete all modules to unlock</p>
              <div className="course-certificate-progress-info">
                <div className="course-certificate-progress-bar">
                  <div className="course-certificate-progress-fill" style={{ width: `${courseInfo.progress}%` }}></div>
                </div>
                <span>{courseInfo.progress}% complete</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Get content for active tab
  const getTabContent = () => {
    switch (activeInteriorTab) {
      case 'overview':
        return renderOverviewTab();
      case 'curriculum':
        return renderCurriculumTab();
      case 'assignments':
        return renderAssignmentsTab();
      case 'discussions':
        return renderDiscussionsTab();
      case 'resources':
        return renderResourcesTab();
      case 'certificates':
        return renderCertificatesTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="course-mockup-content">
      {/* Course Header */}
      <div className="course-mock-header">
        <div className="course-header-icon">
          <GraduationCap size={16} />
        </div>
        <div className="course-header-text">
          <h2 className="course-header-title">{courseInfo.title}</h2>
          <div className="course-header-meta">
            <span className="course-instructor">
              <div className="course-instructor-avatar" style={{ background: avatarColors[0] }}>LH</div>
              {courseInfo.instructor}
            </span>
            <span className="course-rating">
              <Star size={10} />
              {courseInfo.rating}
            </span>
            <span className="course-students">
              <Users size={10} />
              {courseInfo.students}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="course-main-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`course-main-tab ${activeInteriorTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabClick(tab.id)}
          >
            <tab.icon size={11} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="course-content">
        {getTabContent()}
      </div>
    </div>
  );
};

export default CoursesMockupContent;
